/* eslint-disable max-statements */
import { Box3, Group, Object3D, Vector3 } from 'three';

import { Kitchen, Notification } from '@rta/rta-blueprint-library';

import { removeSelectionGroups } from '../ai-tools/selection-label.helper';
import {
	createValidationMarkerSprite,
	VALIDATION_MARKER_GROUP_TYPE,
	VALIDATION_MARKER_SIZE,
	VALIDATION_MARKER_TYPE,
	ValidationMarkerSeverity,
	worstSeverity
} from './validation-marker.helper';

const SEVERITY_BY_TYPE: Record<number, ValidationMarkerSeverity> = { 1: 'info', 2: 'warn', 4: 'error', 8: 'critical' };

/** How far out from the anchor's face the badge floats, and how far below it sits. */
const OUT_IN = 10;
const DROP_IN = VALIDATION_MARKER_SIZE * 0.75;

export type ValidationMarker = {
	/** The notification the badge opens — the worst one when several share an anchor */
	notificationId: string;
	notificationIds: Array<string>;
	anchorId: string;
	severity: ValidationMarkerSeverity;
};

export type ShowValidationMarkersParams = {
	kitchen: Kitchen | null;
	islands: Array<Object3D>;
};

/** Every object in the island groups that carries a domain id, keyed by that id. */
const indexSceneObjects = (islands: Array<Object3D>): Map<string, Object3D> => {
	const byId = new Map<string, Object3D>();
	for (const islandGroup of islands) {
		islandGroup.traverse((child) => {
			const { id, type } = child.userData ?? {};
			if (!id || type === VALIDATION_MARKER_TYPE) return;
			if (!byId.has(String(id))) byId.set(String(id), child);
		});
	}
	return byId;
};

/**
 * Where the badge hangs: the panel item, else the object the notification names, else the island
 * that owns it. That last hop matters for a design saved before `itemRefId` existed — those
 * notifications name only a panel, and a panel's mesh carries a synthetic scene id, so without it
 * nothing anchors and the design shows no badges at all until a validation happens to re-run.
 */
const resolveAnchorId = (notification: Notification, byId: Map<string, Object3D>, kitchen: Kitchen | null): string | null => {
	const direct = [notification.itemRefId, notification.refId]
		.map((candidate) => (candidate ? String(candidate) : ''))
		.find((candidate) => candidate && byId.has(candidate));
	if (direct) return direct;

	const refId = notification.refId ? String(notification.refId) : '';
	const island = refId ? kitchen?.getReferenceMap(refId)?.island ?? null : null;
	const islandId = island ? String(island.id) : '';

	return islandId && byId.has(islandId) ? islandId : null;
};

/** Below the anchor and out towards the viewer, clear of the floor. */
const markerPosition = (anchor: Object3D): Vector3 => {
	const box = new Box3().setFromObject(anchor);
	const position = box.getCenter(new Vector3());

	const outward = new Vector3();
	anchor.getWorldDirection(outward);
	if (outward.lengthSq() < 1e-6) outward.set(0, 0, 1);
	position.add(outward.multiplyScalar(OUT_IN));

	position.y = Math.max(box.min.y - DROP_IN, DROP_IN);
	return position;
};

/**
 * Draw a badge under every appliance column (or island) the blueprint has a validation for,
 * replacing the previous set. Returns what was drawn so the controller can publish it.
 * The badges are rebuilt rather than moved — the rules engine regenerates notifications
 * wholesale, so there is no stable identity to animate between.
 */
export const showValidationMarkersCfn = (params: ShowValidationMarkersParams): Array<ValidationMarker> => {
	const { kitchen, islands } = params;
	if (!islands || islands.length === 0) return [];

	const objectHolder = islands[0].parent as Group | null;
	if (!objectHolder) return [];

	removeSelectionGroups(objectHolder, [VALIDATION_MARKER_GROUP_TYPE]);

	const notifications = kitchen?.design?.notifications?.children ?? [];
	if (notifications.length === 0) return [];

	const byId = indexSceneObjects(islands);
	const grouped = new Map<string, { anchor: Object3D; markers: Array<{ id: string; severity: ValidationMarkerSeverity }> }>();

	for (const notification of notifications) {
		const anchorId = resolveAnchorId(notification, byId, kitchen);
		if (!anchorId) continue;

		const entry = grouped.get(anchorId) ?? { anchor: byId.get(anchorId), markers: [] };
		entry.markers.push({ id: notification.id, severity: SEVERITY_BY_TYPE[Number(notification.type)] ?? 'warn' });
		grouped.set(anchorId, entry);
	}

	if (grouped.size === 0) return [];

	const markersGroup = new Group();
	markersGroup.userData = { type: VALIDATION_MARKER_GROUP_TYPE };
	const drawn: Array<ValidationMarker> = [];

	for (const [anchorId, { anchor, markers }] of grouped) {
		const severity = worstSeverity(markers.map((marker) => marker.severity));
		const notificationIds = markers.map((marker) => marker.id);
		const [notificationId] = notificationIds;

		const sprite = createValidationMarkerSprite(severity, markers.length);
		sprite.position.copy(markerPosition(anchor));
		sprite.userData = { id: notificationId, type: VALIDATION_MARKER_TYPE, anchorId, notificationIds, severity };

		markersGroup.add(sprite);
		drawn.push({ notificationId, notificationIds, anchorId, severity });
	}

	objectHolder.add(markersGroup);
	return drawn;
};

/** Take every badge off the scene. */
export const hideValidationMarkersCfn = (islands: Array<Object3D>): void => {
	const objectHolder = islands?.[0]?.parent as Group | null;
	if (objectHolder) removeSelectionGroups(objectHolder, [VALIDATION_MARKER_GROUP_TYPE]);
};
